/**
 * ALPR preservation demand and Article 39.14 request field coverage for one case.
 *
 * These two letters only go out with a not-guilty letter. `applies` is false on every other case,
 * so the Documents tab can stay quiet on parking and deferral work.
 *
 * The search radius is the one field that can be set from here. Every other missing value lives on
 * the case form, but the radius is a tactical choice made when the request is being prepared, so
 * it belongs beside the warning that names it.
 */

export interface MissingField {
  field: string;
  label: string;
}

export interface FieldCoverageReport {
  document: string;
  ready: boolean;
  missingRequired: MissingField[];
  blankOptional: MissingField[];
}

export interface AlprDocumentsReadinessDto {
  applies: boolean;
  /** What the case holds. Null until somebody picks one, and what the document prints. */
  searchRadiusMiles: number | null;
  /** What to start the box at when the case holds nothing. Never a recorded value. */
  suggestedSearchRadiusMiles: number;
  preservationDemand: FieldCoverageReport;
  article3914Request: FieldCoverageReport;
}

export default class AlprDocumentsApi {
  private static host(): string {
    return process.env.API_HOST?.trim() || window.location.origin;
  }

  static async readiness(ticketGuid: string): Promise<AlprDocumentsReadinessDto> {
    const response = await fetch(
      `${AlprDocumentsApi.host()}/api/v1/manager/ticket/${ticketGuid}/alpr-documents/readiness`,
      { credentials: 'include' },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || err.message || 'Failed to check ALPR document readiness');
    }
    return response.json();
  }

  /** `null` clears it, putting the case back to not-ready rather than printing a blank. */
  static async setSearchRadius(
    ticketGuid: string,
    miles: number | null,
  ): Promise<{ Ticket_AlprSearchRadiusMiles: number | null }> {
    const response = await fetch(
      `${AlprDocumentsApi.host()}/api/v1/manager/ticket/${ticketGuid}/alpr-search-radius`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ Ticket_AlprSearchRadiusMiles: miles }),
      },
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || err.error || 'Failed to set the search radius');
    }
    return response.json();
  }
}
